import { Tabs as AntTabs, Avatar, Space, Tag, Typography } from 'antd';
import { Interval } from 'luxon';
import { useEffect, useState } from 'react';

import {
    ShiftListItemDto,
    ShiftStatus,
    ShiftTime,
    ShiftType,
} from '@clh/api-client';
import { useApiClient } from '@clh/ui';
import {
    getEstimatedMinutesToBeWorkedFromShiftLength,
    getShiftDuration,
    toHourAndMinuteDisplay,
} from '@clh/util';

import { getDateStrWithTimezone } from '../../helpers';
import { useSelection } from '../../hooks/use-selection';
import ProfileNameMenu from '../../shared/profile-name-menu';
import { SHIFT_TYPE_LABEL } from '../shift-type-label';

import AssignmentTab from './assignment';
import CostsTab from './costs';
import DetailsTab from './details';
import PayTab from './pay';
import RatingsTab from './ratings';
import RequestsTab from './requests';

function getShiftStatus(shift: ShiftListItemDto) {
    if (shift.status === ShiftStatus.Canceled) {
        return { label: 'Canceled', color: 'error' as const };
    }

    if (!shift.nurseProfile) {
        return { label: 'Unassigned', color: 'warning' as const };
    }

    const totalMinutesWorked =
        (shift.minutesWorked || 0) + (shift.onCallShift?.minutesWorked || 0);

    if (totalMinutesWorked) {
        return {
            label: `Time submitted: ${toHourAndMinuteDisplay(totalMinutesWorked, true)}`,
            color: 'success' as const,
        };
    }

    const now = new Date();

    if (shift.startTime > now) {
        return { label: 'Scheduled to work', color: 'success' as const };
    }

    if (shift.endTime < now) {
        return { label: 'Awaiting timecard', color: 'success' as const };
    }

    return { label: 'Currently working', color: 'success' as const };
}

function getScheduledMinutes(shift: ShiftListItemDto) {
    if (shift.shiftTime === ShiftTime.Custom) {
        return getEstimatedMinutesToBeWorkedFromShiftLength(
            Interval.fromDateTimes(shift.startTime, shift.endTime).toDuration(
                'minutes'
            ).minutes
        );
    }

    return getShiftDuration(shift.shiftTime) * 60;
}

export default function Tabs() {
    const api = useApiClient();
    const { selection } = useSelection<ShiftListItemDto>();

    const nurseProfileId = selection.nurseProfile?.id;
    const timezone = selection.facility.address.timezone;

    const [avatarUrl, setAvatarUrl] = useState<string>();

    useEffect(() => {
        if (!api || !nurseProfileId) return;

        api.profileControllerGetAvatar({ profileId: nurseProfileId })
            .then(({ avatar }) => setAvatarUrl(avatar ?? undefined))
            .catch(() => setAvatarUrl(undefined));
    }, [api, nurseProfileId]);

    const status = getShiftStatus(selection);
    const scheduledMinutes = getScheduledMinutes(selection);

    const startStr = getDateStrWithTimezone(selection.startTime, timezone);
    const endStr = getDateStrWithTimezone(selection.endTime, timezone);

    return (
        <>
            <Space
                direction="vertical"
                align="center"
                style={{ width: '100%', marginBottom: 16 }}
            >
                {selection.nurseProfile ? (
                    <>
                        <Avatar size="large" src={avatarUrl}>
                            {selection.nurseProfile.firstName[0]}{' '}
                            {selection.nurseProfile.lastName[0]}
                        </Avatar>
                        <ProfileNameMenu
                            profileId={selection.nurseProfile.id}
                            firstName={selection.nurseProfile.firstName}
                            lastName={selection.nurseProfile.lastName}
                            titleStyle={{ margin: 0 }}
                        />
                    </>
                ) : (
                    <Typography.Title level={4} style={{ margin: 0 }}>
                        Unassigned
                    </Typography.Title>
                )}

                <Typography.Text strong>
                    {selection.facility.name}
                </Typography.Text>

                <Typography.Text type="secondary">
                    {startStr} - {endStr}
                </Typography.Text>

                <Typography.Text type="secondary">
                    {toHourAndMinuteDisplay(scheduledMinutes, true)} scheduled
                </Typography.Text>

                <Space>
                    <Tag>{SHIFT_TYPE_LABEL[selection.shiftType]}</Tag>
                    <Tag color={status.color}>{status.label}</Tag>
                </Space>
            </Space>

            <AntTabs
                items={[
                    {
                        label: 'Details',
                        key: 'tab-details',
                        children: <DetailsTab />,
                    },
                    {
                        label: 'Costs',
                        key: 'tab-costs',
                        children: <CostsTab />,
                    },
                    {
                        label: 'Assignment',
                        key: 'tab-assignment',
                        children: <AssignmentTab />,
                    },
                    {
                        label: 'Requests',
                        key: 'tab-requests',
                        children: <RequestsTab />,
                    },
                    ...(selection.shiftType === ShiftType.Orientation
                        ? []
                        : [
                              {
                                  label: 'Ratings',
                                  key: 'tab-ratings',
                                  children: <RatingsTab />,
                              },
                          ]),
                    {
                        label: 'Pay',
                        key: 'tab-pay',
                        children: <PayTab />,
                    },
                ]}
            />
        </>
    );
}
